import { useState, useEffect, useMemo, Fragment } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import MainButton from 'components/Buttons/MainButton'
import AlertDialog from 'components/AlertDialog'
import SyncAgendaModal from 'pages/Modals/SyncAgendaModal'
import { Formik } from 'formik'
import editAvaliabilityValidator from 'validators/editAvaliabilityValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  Container,
  Form,
  Row,
  CheckRow,
  SubTitle,
  FormField,
  Label,
  WarningText,
  ErrorContainer
} from '../../styles'

export default function EditWorkingDays () {
  const [loading, setLoading] = useState (true)
  const [business, setBusiness] = useState ({})
  const [weekdays, setWeekdays] = useState ([])
  const [workingDays, setWorkingDays] = useState ([])
  const [openingTimeWindows, setOpeningTimeWindows] = useState ([])
  const [closingTimeWindows, setClosingTimeWindows] = useState ([])
  const [onlineOpeningTimeWindows, setOnlineOpeningTimeWindows] = useState ({})
  const [onlineClosingTimeWindows, setOnlineClosingTimeWindows] = useState ({})
  const [cutileiOpeningTimeWindows, setCutileiOpeningTimeWindows] = useState ({})
  const [cutileiClosingTimeWindows, setCutileiClosingTimeWindows] = useState ({})
  const [professionals, setProfessionals] = useState ([])
  const [filteredProfessionals, setFilteredProfessionals] = useState ([])
  const [errorMessage, setErrorMessage] = useState (null)
  const [showSyncDialog, setShowSyncDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const hasEditPermission = AuthService.hasPermission ('business_config.agenda', 'edit')
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const navigate = useNavigate ()
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    setLoading (true)
    
    try {
      const [
        { data: business },
        { data: weekdays },
        { data: workingDays },
        { data: professionals }
      ] = await Promise.all ([
        cutileiApi.get (`/businesses/${businessId}`, requestConfig),
        cutileiApi.get ('/weekdays'),
        cutileiApi.get (`/businesses/${businessId}/working_days`),
        cutileiApi.get (
          `/businesses/${businessId}/professionals?access_level=service&status=active&deleted=false`,
          requestConfig
        )
      ])
      
      const timeWindows = TimeWindow.getTimeWindows ('6:00', '22:00')
      const onlineTimeWindows = {...weekdays.map (() => timeWindows)}

      setBusiness (business)
      setWeekdays (weekdays)
      setWorkingDays (workingDays)
      setOpeningTimeWindows (timeWindows)
      setClosingTimeWindows (timeWindows)
      setProfessionals (professionals)
      setOnlineOpeningTimeWindows (onlineTimeWindows)
      setOnlineClosingTimeWindows (onlineTimeWindows)
      setCutileiOpeningTimeWindows (onlineTimeWindows)
      setCutileiClosingTimeWindows (onlineTimeWindows)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleSyncDialog = () => setShowSyncDialog (!showSyncDialog)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)
  const returnToHome = () => navigate ('/business', { replace: true })

  if (loading) return <Loading style={{marginTop: 30}}/>

  return (
    <Formik
      validationSchema={editAvaliabilityValidator}
      initialValues={{
        'professionals': [],
        'enable_online_cancel': business.enable_online_cancel,
        'working_days': weekdays.map (day => {
          const workingDay = workingDays.find (workingDay => workingDay.id === day.id)

          if (workingDay) {
            const opening = TimeWindow.toShortTime (workingDay.opening)
            const closing = TimeWindow.toShortTime (workingDay.closing)
            const online_opening = TimeWindow.toShortTime (workingDay.online_opening || workingDay.opening)
            const online_closing = TimeWindow.toShortTime (workingDay.online_closing || workingDay.closing)
            const cutilei_opening = TimeWindow.toShortTime (workingDay.cutilei_opening || workingDay.opening)
            const cutilei_closing = TimeWindow.toShortTime (workingDay.cutilei_closing || workingDay.closing)
            
            return { 
              ...workingDay,
              opening: { label: opening, value: opening },
              closing: { label: closing, value: closing },
              online_opening: { label: online_opening, value: online_opening },
              online_closing: { label: online_closing, value: online_closing },
              cutilei_opening: { label: cutilei_opening, value: cutilei_opening },
              cutilei_closing: { label: cutilei_closing, value: cutilei_closing },
              selected: true,
              changed: false
            } 
          } else return {
            ...day, opening: '', closing: '', online_opening: '', online_closing: '',
            cutilei_opening: '', cutilei_closing: '', selected: false, changed: false
          }
        })
      }}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        setErrorMessage (null)
        setSubmitting (true)

        try {
          const { professionals, enable_online_cancel } = values

          const working_days = values.working_days.map (day => ({
            ...day,
            opening: day.opening.value,
            closing: day.closing.value, 
            online_opening: day.online_opening.value,
            online_closing: day.online_closing.value,
            cutilei_opening: day.cutilei_opening.value,
            cutilei_closing: day.cutilei_closing.value
          }))

          await cutileiApi.post (`/businesses/${businessId}/working_days`, {
            working_days,
            professionals
          }, requestConfig)

          if (enable_online_cancel !== business.enable_online_cancel) {
            await cutileiApi.put (`/businesses/${businessId}`, {
              enable_online_cancel
            }, requestConfig)
          }

          setSubmitting (false)
          resetForm ({ values })
          toggleOkDialog ()
        } catch (error) {
          setErrorMessage (getApiErrorMessage (error))
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {function WorkingDaysForm ({
        values,
        errors,
        dirty,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        setTouched,
        handleSubmit
      }) {
        const handleDaySelected = (value, index) => {
          let dayToCopy = null

          if (value === true && index > 0) {
            const prevDay = values.working_days[index - 1]
            if (
              prevDay.selected &&
              prevDay.opening && prevDay.closing &&
              prevDay.opening.value && prevDay.closing.value
            ) {
              dayToCopy = prevDay
            }
          }

          const dayValues = {
            opening: dayToCopy?.opening || '',
            closing: dayToCopy?.closing || '',
            online_opening: '',
            online_closing: '',
            cutilei_opening: '',
            cutilei_closing: '',
            accepts_online: false,
            accepts_cutilei: false
          }

          setFieldValue (`working_days.${index}`, {
            ...values.working_days[index],
            ...(value === true && dayValues),
            selected: value,
            changed: true
          })

          if (value === true) setTouched ({
            [`working_days.${index}.opening`]: false,
            [`working_days.${index}.closing`]: false,
            [`working_days.${index}.online_opening`]: false,
            [`working_days.${index}.online_closing`]: false,
            [`working_days.${index}.cutilei_opening`]: false,
            [`working_days.${index}.cutilei_closing`]: false,
            [`working_days.${index}.accepts_online`]: false,
            [`working_days.${index}.accepts_cutilei`]: false
          })
        }

        const handleAcceptsOnlineSelected = (value, index) => {
          let dayToCopy = null

          if (value === true && index > 0) {
            const prevDay = values.working_days[index - 1]
            if (
              prevDay.selected && prevDay.accepts_online &&
              prevDay.online_opening && prevDay.online_closing &&
              prevDay.online_opening.value && prevDay.online_closing.value
            ) {
              dayToCopy = prevDay
            }
          }

          const currentOpening = values.working_days[index].opening
          const currentClosing = values.working_days[index].closing
          let onlineOpening = currentOpening
          let onlineClosing = currentClosing

          if (dayToCopy && currentOpening?.value && currentClosing?.value) {
            const prevOnlineOpening = dayToCopy.online_opening.value
            const prevOnlineClosing = dayToCopy.online_closing.value

            const prevOpeningTime = DateTime.fromFormat (prevOnlineOpening, 'H:mm')
            const prevClosingTime = DateTime.fromFormat (prevOnlineClosing, 'H:mm')
            const currentOpeningTime = DateTime.fromFormat (currentOpening.value, 'H:mm')
            const currentClosingTime = DateTime.fromFormat (currentClosing.value, 'H:mm')
            
            if (prevOpeningTime >= currentOpeningTime && prevClosingTime <= currentClosingTime) {
              onlineOpening = dayToCopy.online_opening
              onlineClosing = dayToCopy.online_closing
            }
          }

          const onlineValues = {
            online_opening: onlineOpening,
            online_closing: onlineClosing
          }

          setFieldValue (`working_days.${index}`, {
            ...values.working_days[index],
            ...value === true ? onlineValues : {
              online_opening: '',
              online_closing: ''
            },
            accepts_online: value,
            changed: true
          })

          if (value === true) {
            const opening = onlineOpening?.value
            const closing = onlineClosing?.value
            const calculatedTimeWindows = TimeWindow.getTimeWindows (opening, closing)
            
            if (opening && closing) {
              setOnlineOpeningTimeWindows (prev => ({ ...prev, [index]: calculatedTimeWindows }))
              setOnlineClosingTimeWindows (prev => ({ ...prev, [index]: calculatedTimeWindows }))
            }
            
            setTouched ({
              [`working_days.${index}.online_opening`]: false,
              [`working_days.${index}.online_closing`]: false
            })
          }
        }

        const handleAcceptsCutileiSelected = (value, index) => {
          let dayToCopy = null

          if (value === true && index > 0) {
            const prevDay = values.working_days[index - 1]
            if (
              prevDay.selected && prevDay.accepts_cutilei &&
              prevDay.cutilei_opening && prevDay.cutilei_closing &&
              prevDay.cutilei_opening.value && prevDay.cutilei_closing.value
            ) {
              dayToCopy = prevDay
            }
          }

          const currentOpening = values.working_days[index].opening
          const currentClosing = values.working_days[index].closing
          let cutileiOpening = currentOpening
          let cutileiClosing = currentClosing

          if (dayToCopy && currentOpening?.value && currentClosing?.value) {
            const prevCutileiOpening = dayToCopy.cutilei_opening.value
            const prevCutileiClosing = dayToCopy.cutilei_closing.value
            
            const prevOpeningTime = DateTime.fromFormat (prevCutileiOpening, 'H:mm')
            const prevClosingTime = DateTime.fromFormat (prevCutileiClosing, 'H:mm')
            const currentOpeningTime = DateTime.fromFormat (currentOpening.value, 'H:mm')
            const currentClosingTime = DateTime.fromFormat (currentClosing.value, 'H:mm')
            
            if (prevOpeningTime >= currentOpeningTime && prevClosingTime <= currentClosingTime) {
              cutileiOpening = dayToCopy.cutilei_opening
              cutileiClosing = dayToCopy.cutilei_closing
            }
          }

          const cutileiValues = {
            cutilei_opening: cutileiOpening,
            cutilei_closing: cutileiClosing
          }

          setFieldValue (`working_days.${index}`, {
            ...values.working_days[index],
            ...value === true ? cutileiValues : {
              cutilei_opening: '',
              cutilei_closing: ''
            },
            accepts_cutilei: value,
            changed: true
          })

          if (value === true) {
            const opening = cutileiOpening?.value
            const closing = cutileiClosing?.value
            const calculatedTimeWindows = TimeWindow.getTimeWindows (opening, closing)
            
            if (opening && closing) {
              setCutileiOpeningTimeWindows (prev => ({ ...prev, [index]: calculatedTimeWindows }))
              setCutileiClosingTimeWindows (prev => ({ ...prev, [index]: calculatedTimeWindows }))
            }
            
            setTouched ({
              [`working_days.${index}.cutilei_opening`]: false,
              [`working_days.${index}.cutilei_closing`]: false
            })
          }
        }

        const handleTimeSelected = (name, option) => {
          const dayIndex = parseInt (name.split ('.')[1])

          if (name.includes ('opening')) {
            setClosingTimeWindows (TimeWindow.getTimeWindows (option.value, '22:00', true))
            setOnlineOpeningTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, values.working_days[dayIndex].closing.value || '22:00'
              )
            }))
            setCutileiOpeningTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, values.working_days[dayIndex].closing.value || '22:00'
              )
            }))
          } else if (name.includes ('closing')) {
            setOpeningTimeWindows (TimeWindow.getTimeWindows ('6:00', option.value, true))
            setOnlineClosingTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                values.working_days[dayIndex].opening.value || '6:00', option.value
              )
            }))
            setCutileiClosingTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                values.working_days[dayIndex].opening.value || '6:00', option.value
              )
            }))
          }

          setFieldValue ('working_days', values.working_days.map ((day, index) => ({
            ...day,
            opening: name.includes ('opening') && day.opening === ''
              ? option : index === dayIndex && name.includes ('opening')
                ? option : day.opening,
            closing: name.includes ('closing') && day.closing === ''
              ? option : index === dayIndex && name.includes ('closing')
                ? option : day.closing,
            changed: index === dayIndex ? true : day.changed
          })))
        }

        const handleOnlineTimeSelected = (name, option) => {
          const dayIndex = parseInt (name.split ('.')[1])
          const lastTimeWindowIndex = onlineClosingTimeWindows[dayIndex].length - 1

          if (name.includes ('online_opening')) {
            setOnlineClosingTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, onlineClosingTimeWindows[dayIndex][lastTimeWindowIndex].value, true
              )
            }))
          } else if (name.includes ('online_closing')) {
            setOnlineOpeningTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                onlineOpeningTimeWindows[dayIndex][0].value, option.value, true
              )
            }))
          }

          setFieldValue ('working_days', values.working_days.map ((day, index) => ({
            ...day,
            online_opening: name.includes ('online_opening') && day.online_opening === ''
              ? option : index === dayIndex && name.includes ('online_opening')
                ? option : day.online_opening,
            online_closing: name.includes ('online_closing') && day.online_closing === ''
              ? option : index === dayIndex && name.includes ('online_closing')
                ? option : day.online_closing,
            changed: index === dayIndex ? true : day.changed
          })))
        }

        const handleCutileiTimeSelected = (name, option) => {
          const dayIndex = parseInt (name.split ('.')[1])
          const lastTimeWindowIndex = cutileiClosingTimeWindows[dayIndex].length - 1

          if (name.includes ('cutilei_opening')) {
            setCutileiClosingTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, cutileiClosingTimeWindows[dayIndex][lastTimeWindowIndex].value, true
              )
            }))
          } else if (name.includes ('cutilei_closing')) {
            setCutileiOpeningTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                cutileiOpeningTimeWindows[dayIndex][0].value, option.value, true
              )
            }))
          }

          setFieldValue ('working_days', values.working_days.map ((day, index) => ({
            ...day,
            cutilei_opening: name.includes ('cutilei_opening') && day.cutilei_opening === ''
              ? option : index === dayIndex && name.includes ('cutilei_opening')
                ? option : day.cutilei_opening,
            cutilei_closing: name.includes ('cutilei_closing') && day.cutilei_closing === ''
              ? option : index === dayIndex && name.includes ('cutilei_closing')
                ? option : day.cutilei_closing,
            changed: index === dayIndex ? true : day.changed
          })))
        }

        const hasCutileiChange = useMemo (() => {
          return values.working_days.some (day => {
            const original = workingDays.find (wd => wd.id === day.id)
            if (!original) return false

            const originalOpening = TimeWindow.toShortTime (original.cutilei_opening || original.opening)
            const originalClosing = TimeWindow.toShortTime (original.cutilei_closing || original.closing)

            return day.changed && (
              (day.cutilei_opening?.value ?? '') !== (originalOpening ?? '') ||
              (day.cutilei_closing?.value ?? '') !== (originalClosing ?? '') ||
              day.accepts_cutilei !== original.accepts_cutilei
            )
          })
        }, [values.working_days, workingDays])

        const openSyncModal = () => {
          if (hasCutileiChange) {
            setFilteredProfessionals (professionals.filter (prof => prof.accepts_cutilei))
          } else {
            setFilteredProfessionals (professionals)
          }
          toggleSyncDialog ()
        }

        return (
          <Container>
            <Form>
              <SubTitle>Disponibilidade de atendimento da agenda do salão</SubTitle>
              <Row style={{alignItems: 'flex-start'}}>
                {values.working_days.map ((day, index) => (
                  <FormField key={day.id}>
                    <CheckRow style={{marginBottom: -5}}>
                      <CheckBox
                        id={`working_days.${index}.selected`}
                        value={values.working_days[index].selected}
                        onValueChange={value => handleDaySelected (value, index)}
                        disabled={!hasEditPermission}
                      />
                      <Label htmlFor={`working_days.${index}.selected`}>
                        {day.name}
                      </Label>
                    </CheckRow>
                    {values.working_days[index].selected && (
                      <Fragment>
                        <Select
                          name={`working_days.${index}.opening`}
                          placeholder='Abre...'
                          value={values.working_days[index].opening}
                          options={openingTimeWindows}
                          onChange={handleTimeSelected}
                          onBlur={setFieldTouched}
                          containerStyles={{marginTop: '10px'}}
                          error={errors.working_days?.[index]}
                          touched={touched.working_days?.[index]}
                          isDisabled={!hasEditPermission}
                        />
                        {touched.working_days?.[index]?.opening && errors.working_days?.[index]?.opening && (
                          <WarningText>{errors.working_days[index].opening}</WarningText>
                        )}
                        <Select
                          name={`working_days.${index}.closing`}
                          placeholder='Fecha...'
                          value={values.working_days[index].closing}
                          options={closingTimeWindows}
                          onChange={handleTimeSelected}
                          onBlur={setFieldTouched}
                          containerStyles={{marginTop: '10px'}}
                          error={errors.working_days?.[index]}
                          touched={touched.working_days?.[index]}
                          isDisabled={!hasEditPermission}
                        />
                        {touched.working_days?.[index]?.closing && errors.working_days?.[index]?.closing && (
                          <WarningText>{errors.working_days[index].closing}</WarningText>
                        )}
                      </Fragment>
                    )}
                  </FormField>
                ))}
              </Row>
              {values.working_days.some (day => day.selected) && (
                <Fragment>
                  <SubTitle>Disponibilidade de atendimento para a Cutilei</SubTitle>
                  <Row style={{alignItems: 'flex-start'}}>
                    {values.working_days.map ((day, index) => (
                      day.selected ? (
                        <FormField key={index}>
                          <CheckRow style={{marginBottom: -5}}>
                            <CheckBox
                              id={`working_days.${index}.accepts_cutilei`}
                              value={day.accepts_cutilei}
                              onValueChange={value => handleAcceptsCutileiSelected (value, index)}
                              disabled={!hasEditPermission}
                            />
                            <Label htmlFor={`working_days.${index}.accepts_cutilei`}>
                              {day.name}
                            </Label>
                          </CheckRow>
                          {day.accepts_cutilei && (
                            <Fragment>
                              <Select
                                name={`working_days.${index}.cutilei_opening`}
                                placeholder='Abre...'
                                value={day.cutilei_opening}
                                options={cutileiOpeningTimeWindows[index]}
                                onChange={handleCutileiTimeSelected}
                                onBlur={setFieldTouched}
                                containerStyles={{marginTop: '10px'}}
                                error={errors.working_days?.[index]}
                                touched={touched.working_days?.[index]}
                                isDisabled={!hasEditPermission}
                              />
                              {touched.working_days?.[index]?.cutilei_opening && errors.working_days?.[index]?.cutilei_opening && (
                                <WarningText>{errors.working_days[index].cutilei_opening}</WarningText>
                              )}
                              <Select
                                name={`working_days.${index}.cutilei_closing`}
                                placeholder='Fecha...'
                                value={day.cutilei_closing}
                                options={cutileiClosingTimeWindows[index]}
                                onChange={handleCutileiTimeSelected}
                                onBlur={setFieldTouched}
                                containerStyles={{marginTop: '10px'}}
                                error={errors.working_days?.[index]}
                                touched={touched.working_days?.[index]}
                                isDisabled={!hasEditPermission}
                              />
                              {touched.working_days?.[index]?.cutilei_closing && errors.working_days?.[index]?.cutilei_closing && (
                                <WarningText>{errors.working_days[index].cutilei_closing}</WarningText>
                              )}
                            </Fragment>
                          )}
                        </FormField>
                      ) : (
                        <FormField key={index}/>
                      )
                    ))}
                  </Row>
                  <SubTitle>Disponibilidade de atendimento para agendamentos online</SubTitle>
                  <Row style={{alignItems: 'flex-start', marginBottom: 0}}>
                    {values.working_days.map ((day, index) => (
                      day.selected ? (
                        <FormField key={index}>
                          <CheckRow style={{marginBottom: -5}}>
                            <CheckBox
                              id={`working_days.${index}.accepts_online`}
                              value={day.accepts_online}
                              onValueChange={value => handleAcceptsOnlineSelected (value, index)}
                              disabled={!hasEditPermission}
                            />
                            <Label htmlFor={`working_days.${index}.accepts_online`}>
                              {day.name}
                            </Label>
                          </CheckRow>
                          {day.accepts_online && (
                            <Fragment>
                              <Select
                                name={`working_days.${index}.online_opening`}
                                placeholder='Abre...'
                                value={day.online_opening}
                                options={onlineOpeningTimeWindows[index]}
                                onChange={handleOnlineTimeSelected}
                                onBlur={setFieldTouched}
                                containerStyles={{marginTop: '10px'}}
                                error={errors.working_days?.[index]}
                                touched={touched.working_days?.[index]}
                                isDisabled={!hasEditPermission}
                              />
                              {touched.working_days?.[index]?.online_opening && errors.working_days?.[index]?.online_opening && (
                                <WarningText>{errors.working_days[index].online_opening}</WarningText>
                              )}
                              <Select
                                name={`working_days.${index}.online_closing`}
                                placeholder='Fecha...'
                                value={day.online_closing}
                                options={onlineClosingTimeWindows[index]}
                                onChange={handleOnlineTimeSelected}
                                onBlur={setFieldTouched}
                                containerStyles={{marginTop: '10px'}}
                                error={errors.working_days?.[index]}
                                touched={touched.working_days?.[index]}
                                isDisabled={!hasEditPermission}
                              />
                              {touched.working_days?.[index]?.online_closing && errors.working_days?.[index]?.online_closing && (
                                <WarningText>{errors.working_days[index].online_closing}</WarningText>
                              )}
                            </Fragment>
                          )}
                        </FormField>
                      ) : (
                        <FormField key={index}/>
                      )
                    ))}
                  </Row>
                  <CheckRow style={{marginTop: 12}}>
                    <CheckBox
                      id='enable_online_cancel'
                      value={values.enable_online_cancel}
                      onValueChange={value => setFieldValue ('enable_online_cancel', value)}
                    />
                    <Label htmlFor='enable_online_cancel'>
                      Permitir cancelamento de agendamentos online pelos clientes
                    </Label>
                  </CheckRow>
                </Fragment>
              )}
              {errorMessage && (
                <ErrorContainer>
                  <p>{errorMessage}</p>
                </ErrorContainer>
              )}
              {hasEditPermission && (dirty && values.working_days.some (day => day.selected)) && (
                <MainButton
                  type='button'
                  label='Salvar alterações'
                  onClick={
                    professionals.length > 0 && values.working_days.some (day => day.changed)
                      ? openSyncModal
                      : handleSubmit
                  }
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  width={160}
                  style={{marginTop: 15}}
                />
              )}
              <SyncAgendaModal
                visible={showSyncDialog}
                professionals={filteredProfessionals}
                onConfirm={professionals => {
                  setFieldValue ('professionals', professionals)
                  handleSubmit ()
                }}
                onClose={toggleSyncDialog}
              />
              <AlertDialog
                visible={showOkDialog}
                title='Sucesso'
                message={isTutorial ? 'Agenda configurada!' : 'Agenda alterada!'}
                confirmText='Ok'
                onConfirm={isTutorial ? returnToHome : toggleOkDialog}
                onClose={isTutorial ? returnToHome : toggleOkDialog}
              />
            </Form>
          </Container>
        )
      }}
    </Formik>
  )
}
